import React, { lazy, Suspense, useCallback, useEffect, useLayoutEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import cx from 'classnames';
import PropTypes from 'prop-types';

import {
  BetManager,
  Bus,
  constants,
  globalBetslipStateUpdate,
  SettingsManager,
  SiteConfigManager,
  useApplicationState,
  useBetslipState,
  useGlobalBetslipState,
  useSettings,
} from '@atlas/core';
import { EventLink, staticRoutes } from '@atlas/routing';
import { clientProfileRoutes, COMPONENT_TYPES } from '@atlas/routing';
import {
  BetReports,
  Button,
  Icon,
  Label,
  Tab,
  TabContent,
  TabHiddenContent,
  TabNavigator,
  useTabNavigator,
} from '@atlas/ui';

import BetOutcomeLink from 'common/BetOutcomeLink';
import { useLocalBetslip } from 'common/Betslip/LocalBetslipContext';
import NotificationStatePopup from 'common/Popup/NotificationStatePopup';
import { useSignupPopup } from 'src/components/Auth/useSignUpPopup';

import BetslipClassicView from './BetslipClassic';
import BetslipEuropeanView from './BetslipEuropean';
import BetslipPopupWrapper from './BetslipPopupWrapper';
import BetslipSwitchers from './BetslipSwitchers';
import GlobalBetslipLayout from './GlobalBetslipLayout';
import QuickBet from './QuickBet';

const MyOrders = lazy(
  () => import(/* webpackChunkName: "History" */ 'common/History/MyOrders/MyOrdersBetslipPopup'),
);
const MyBets = lazy(() => import('common/History/MyBets'));

const { CLIENT_PROFILE } = COMPONENT_TYPES;
const { history } = clientProfileRoutes;

const removeAllBetItems = () => BetManager.clearBets();
const cleanUpReports = () => globalBetslipStateUpdate({ reports: [] });

const AuthenticatedView = ({ children }) => {
  const { authenticated } = useApplicationState();
  const { setIsOpenPopup } = useLocalBetslip();
  const { openSignupPopup } = useSignupPopup();

  const signUpPopup = SiteConfigManager.getConfig('config.config.registration.showInPopup', false);

  if (!authenticated) {
    return (
      <Suspense fallback={<div />}>
        <NotificationStatePopup
          buttons={
            <>
              <Link className="secondary" to={staticRoutes.signin}>
                <Button
                  label="Sign in"
                  onClick={() => setIsOpenPopup(false)}
                  size="big"
                  type="secondary"
                />
              </Link>
              {SiteConfigManager.getConfig('config.config.enable_registration') &&
                (signUpPopup ? (
                  <Button
                    className="mybets-signup-button"
                    label="Sign up"
                    onClick={openSignupPopup}
                    size="big"
                    type="primary"
                  />
                ) : (
                  <Link className="primary" to={staticRoutes.signup}>
                    <Button
                      label="Sign up"
                      onClick={() => setIsOpenPopup(false)}
                      size="big"
                      type="primary"
                    />
                  </Link>
                ))}
            </>
          }
          message="To see your bets please sign up or sign in"
          title="You are not logged into your account"
        />
      </Suspense>
    );
  }

  return children;
};

const BetslipTabs = ({ betItems, onCloseHandler, orderBet }) => {
  const [tabIndex] = useTabNavigator();

  return (
    <div className={cx('popup-with-tabs', `bets__${tabIndex}`)}>
      <div className="tabs-list">
        <Tab index={0}>
          <Label message="betslip" />
          {betItems.length > 0 && <span className="bet-count">{betItems.length}</span>}
        </Tab>

        <Tab index={1}>
          <Label dataTestId="betslip-my-bets-tab" message="my_bets" />
        </Tab>

        {orderBet && (
          <Tab index={2}>
            <Label message="my_orders" />
          </Tab>
        )}

        <button
          className="tab-list-item close-betslip-container"
          data-testid="close-betslip"
          onClick={onCloseHandler}
        >
          <Icon className="icon-close" height={30} name="arrow-down" width={30} />
        </button>
      </div>
    </div>
  );
};

BetslipTabs.propTypes = {
  betItems: PropTypes.array.isRequired,
  onCloseHandler: PropTypes.func.isRequired,
  orderBet: PropTypes.bool,
};

function BetslipViewController(props) {
  const { quickBet: isQuickBet } = useSettings();

  const { betIdToEdit, isOpenPopup, setIsOpenPopup, setIsOrderBet } = useLocalBetslip();

  const [tabIndex, setTabIndex] = useTabNavigator();

  const singleBetPopup = SiteConfigManager.getConfig('config.config.betslip.single_bet_popup');
  useEffect(() => {
    const betslipSnapshotHandler = ({ addition, betItems }) => {
      if (addition) {
        globalBetslipStateUpdate({ reports: [] });

        if (betItems && betItems.length === 1 && BetManager.target === 'main' && singleBetPopup) {
          setIsOpenPopup(true);
        }
      }
      if (betItems && betItems.length === 0) {
        setIsOrderBet(false);
      }
      // Switching tab navigator index to betslip
      setTabIndex(0);
    };
    // switch on betslip when the first bet item was added
    Bus.on(`betslip:main:${Bus.events.bet.snapshot}`, betslipSnapshotHandler);

    return () => {
      Bus.off(`betslip:main:${Bus.events.bet.snapshot}`, betslipSnapshotHandler);
    };
  }, []);

  useEffect(() => {
    let betslipToFocus = 'main';
    if (betIdToEdit) {
      betslipToFocus = 'edit';
    } else if (isQuickBet) {
      betslipToFocus = 'quick';
    }
    if (BetManager.target !== betslipToFocus) {
      BetManager.focus(betslipToFocus);
    }
  }, [isQuickBet, betIdToEdit]);

  useLayoutEffect(() => {
    if (isOpenPopup && !isQuickBet) {
      const element = document.querySelector('.bet-items');

      if (element) {
        element.scrollTop = element.scrollHeight;
      }
    }
  }, [isOpenPopup, tabIndex, isQuickBet]);

  return props.children;
}

export default function Betslip() {
  const clientProfilePath = SiteConfigManager.getComponentPath(CLIENT_PROFILE);
  const { quickBet: isQuickBet } = useSettings();

  const { betIdToEdit, isOrderBet, setBetIdToEdit, setIsOpenPopup } = useLocalBetslip();

  const { betItems, settings } = useBetslipState();
  const { reports } = useGlobalBetslipState();
  const { orderBet } = settings;

  const onCloseHandler = useCallback(() => {
    if (isQuickBet) {
      SettingsManager.updateSettings({ quickBet: false });
    }

    if (reports && reports.length > 0) {
      // reset bet state if betslip was submitted
      cleanUpReports();
      removeAllBetItems();
    }
    if (betIdToEdit) {
      setBetIdToEdit(null);
    }
    setIsOpenPopup(false);
  }, [isQuickBet, reports, betIdToEdit, setIsOpenPopup, setBetIdToEdit]);

  // TODO: Rework container classnames
  const popupWrapperClassName = cx('betslip-popup-container', {
    empty: betItems.length === 0,
    'has-items': betItems.length > 1,
    'quick-bet': isQuickBet,
    single: betItems.length === 1,
    'single-order-bet': isOrderBet,
    'single-place-bet': reports.length === 1 && reports[0].betType === constants.BET_TYPE.SINGLE,
  });

  const betslipTabContentClassName = cx({ fullscreen: betItems.length > 1 });

  const BetslipView = useMemo(() => {
    if (isQuickBet) {
      return QuickBet;
    }

    if (settings.mobileType === 'european') {
      return BetslipEuropeanView;
    }

    return BetslipClassicView;
  }, [isQuickBet, settings.mobileType]);

  return (
    <Suspense fallback={<div />}>
      <BetslipPopupWrapper className={popupWrapperClassName} onClose={onCloseHandler}>
        <TabNavigator>
          <BetslipViewController>
            <TabHiddenContent className={betslipTabContentClassName} index={0}>
              <GlobalBetslipLayout>
                <BetslipSwitchers />
                <BetReports BetOutcomeLink={BetOutcomeLink} EventLink={EventLink} isMobile />

                <BetslipView />
              </GlobalBetslipLayout>
            </TabHiddenContent>

            <TabContent className="fullscreen" index={1}>
              <AuthenticatedView>
                <div className="my-bets-container">
                  <Suspense fallback={<div />}>
                    <MyBets />
                  </Suspense>

                  <div className="sticky-bottom-buttons-container">
                    <Link
                      onClick={() => {
                        setIsOpenPopup(false);
                      }}
                      to={`${clientProfilePath}/${history}/bet-history`}
                    >
                      <Button label="Bet history" size="big" type="secondary" />
                    </Link>
                  </div>
                </div>
              </AuthenticatedView>
            </TabContent>

            {orderBet && (
              <TabContent className="fullscreen" index={2}>
                <AuthenticatedView>
                  <Suspense fallback={<div />}>
                    <MyOrders BetOutcomeLink={BetOutcomeLink} />
                  </Suspense>
                </AuthenticatedView>
              </TabContent>
            )}
          </BetslipViewController>

          <BetslipTabs betItems={betItems} onCloseHandler={onCloseHandler} orderBet={orderBet} />
        </TabNavigator>
      </BetslipPopupWrapper>
    </Suspense>
  );
}
